import {action} from 'mobx';

/**
 * The order of registerWebRTCAdapters and DetectWebRtcService is important.
 * Because registerWebRTCAdapters imports IE shim file if needed, and it is required to be able to detect WebRTC in IE.
 **/
//TODO - Infra: Need to put WebRTC detection inside media-service-itself, and remove this lines that are error prone.
import {registerWebRTCAdapters} from '@techsee/techsee-media-service/lib/MediaUtils/Compatibility';
registerWebRTCAdapters();
import {
    DETECT_RTC_ADVISED_TIMEOUT,
    DetectWebRtcService,
    IWebRtcDetector
} from '@techsee/techsee-client-infra/lib/services/DetectWebRtcService';
import {EVENT_SOURCES, LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {BrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {
    BrowserUtilsExtended,
    IBrowserUtilsExtended
} from './services/browser-utils-extended/browser-utils-extended.service';
import {DomUtilsService, IDomUtilsService} from '@techsee/techsee-client-infra/lib/services/DomUtilsService';
import {
    UrlEnvData,
    TsUrlConfigService,
    ITsUrlConfigService
} from '@techsee/techsee-client-infra/lib/services/urlConfig';
import {ScreenType} from '@techsee/techsee-common/lib/constants/visual-journey.constants';
// @ts-ignore
import {getBackendUrl} from '@techsee/techsee-common/lib/utils';
import {
    AuthService as AuthApiService,
    IAuthService as IAuthApiService
} from '@techsee/techsee-client-infra/lib/services/AuthService';
import {IApplicationService, ApplicationService} from './services/ApplicationService';
import {ILocalizationService, LocalizationService} from './services/LocalizationService';
import {IRedirectionService, RedirectionService} from './services/RedirectionService';
import {ISessionTagsService, SessionTagsService} from './services/SessionTagsService';
import {ITechseeSocketManager, TechseeSocketManager} from '@techsee/techsee-client-infra/lib/infra/SocketManager';
import {IEventLogsService, EventLogsService} from './services/EventsLogService';
import {AppState} from './app.state';
import {IUiFlavorService, UiFlavorService} from './services/ts-ui-flavor/ts-ui-flavor.service';
import {TsAppHistoryService} from './services/TsAppHistoryService';
import {IHistoryService, IVideoRecordingsService} from './states/history/_contracts/HistoryServiceContracts';
import {TsAppVideoRecordingsService} from './services/TsAppVideoRecordingsService';
import {TsAppUserService, ITsAppUserService} from './services/TsAppUserService';
import {IAppBootstrapParams} from './app.contracts';
import {IAuthExpiryService, AuthExpiryService} from './services/ts-auth-expiry/ts-auth-expiry.service';
import {ChatApi, IChatApi} from './services/ts-chat-api/ChatApi';
import {createFieldModel, getRootStore} from './app.bootstrap';
import {AgentSettingsController} from './layouts/main-layout/agent-settings/controller';
import {AccountSocketService, IAccountSocketService} from './services/AccountSocket/AccountSocketService';
import {SpeedtestService, ISpeedtestService} from './services/SpeedtestService';
import {JsApiService, IJsApiService} from './services/JsApiService';
import {ITsEnvironmentDetect} from '@techsee/techsee-common/lib/helpers/ts-environment-detect';
import {TsTimedOperationService} from './services/ts-timed-operation/ts-timed-operation.service';
import {IVisibilityChange, TsVisibilityChange} from './services/ts-visibility-change/ts-visibility-change.service';
import {
    ITsCanvasAnnotateService,
    TsCanvasAnnotateService
} from './services/ts-canvas-annotate/ts-canvas-annotate.service';
import {
    IInteractionSummaryService,
    InteractionSummaryService
} from '@techsee/techsee-client-infra/lib/services/InteractionSummaryService';
import tsDate from '@techsee/techsee-date';
import {IWifiAnalyzerService, WifiAnalyzerService} from './services/AngularServices/WifiAnalyzerService';
import {TsBusyService, ITsBusyService} from './services/ts-busy/ts-busy.service';
import {
    TsResponsiveUtilsService,
    ITsResponsiveUtilsService
} from './services/ts-responsive-utils/ts-responsive-utils.service';
import {
    EventLogService as EventLogServiceInfra,
    IEventLogService as IEventLogServiceInfra
} from '@techsee/techsee-client-infra/lib/services/EventLogService';
import {EventLogService, IEventLogService} from './services/event-log/event-log.service';
import {IAuthService, AuthService} from '../_react_/services/auth/auth.service';
import {CustomStringService, ICustomStringService} from '@techsee/techsee-client-infra/lib/services/CustomString';
import {
    ITsTranslationHelper,
    TsTranslationHelper
} from './services/ts-translation-helper/ts-translation-helper.service';
import {
    TsInterfaceHelperService,
    ITsInterfaceHelperService
} from './services/ts-interface-helper/ts-interface-helper.service';
import {TsExtraLoggingService, ITsExtraLoggingService} from './services/ts-extra-logging/ts-extra-logging.service';
import {IQosService, QosService} from '@techsee/techsee-client-infra/lib/services/QosService';
import {
    WifiAnalyzerService as WifiAnalyzerApiService,
    IWifiAnalyzerService as IWifiAnalyzerApiService
} from '@techsee/techsee-client-infra/lib/services/WifiAnalyzerService';
import {
    HistoryConsoleService,
    IHistoryConsoleService
} from '@techsee/techsee-client-infra/lib/services/HistoryConsoleService';
import {AgentQOSService, IAgentQOSService} from './services/qos/agent-qos.service';
import {ITsLocaleHelperService, TsLocaleHelperService} from './services/ts-locale-helper/ts-locale-helper.service';
import {ITsSessionData, TsSessionData} from './services/ts-session-data/ts-session-data.service';
import TsVideoControlService from './services/ts-video-control/ts-video-control.service';

import {
    AccountBrandingService,
    IAccountBrandingService
} from '@techsee/techsee-client-infra/lib/services/AccountBrandingService';
import {AppEventsHub} from './services/AppEventsHub';
import {IUserService, UserService} from '@techsee/techsee-client-infra/lib/services/UserService';
import {AccountService, IAccountService} from '@techsee/techsee-client-infra/lib/services/AccountService';
import {IShortUrlService, ShortUrlService} from '@techsee/techsee-client-infra/lib/services/ShortUrlService';
import {IRoomService, RoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {EmailService, IEmailService} from '@techsee/techsee-client-infra/lib/services/EmailService';
import {
    HistoryService as HistoryApiService,
    IHistoryService as IHistoryApiService
} from '@techsee/techsee-client-infra/lib/services/HistoryService';
import {
    ScanService as ScanApiService,
    IScanService as IScanApiService
} from '@techsee/techsee-client-infra/lib/services/ScanService';
import {
    SmsService as SmsApiService,
    ISmsService as ISmsApiService
} from '@techsee/techsee-client-infra/lib/services/smsService';
import {
    IObservationsService,
    ObservationsService
} from '@techsee/techsee-client-infra/lib/services/ObservationsService';
import {
    SsoService as SsoApiService,
    ISsoService as ISsoApiService
} from '@techsee/techsee-client-infra/lib/services/SSOService';
import {ResourcesServices, IResourcesService} from '@techsee/techsee-client-infra/lib/services/ResourcesServices';
import {S3Service, IS3Service} from '@techsee/techsee-client-infra/lib/services/S3Service';
import {GeneralApiService, IGeneralApiService} from '@techsee/techsee-client-infra/lib/services/GeneralApiService';
import {AuthHandlerService} from './services/auth/auth-handler.service';
import {TsReactStateHelper, ITsReactStateHelper} from './services/ts-react-state-helper/ts-react-state-helper.service';

export type AppStoreSettings = {
    env: UrlEnvData;
    browserInfo: any;
};

export enum InitializationState {
    NOT_STARTED = 'not_started',
    INITIALIZING = 'initializing',
    COMPLETED = 'completed'
}

export interface IAppRootStore {
    initRootServices(settings: AppStoreSettings): void;

    getDateFormattedByAcc(date: string, format: string): string;

    getDateFormattedByAccLocal(date: string, format: string): string;

    setSafeApply(safeApply: any): void;

    readonly globals: {
        window: Window;
    };

    readonly initRootServicesCompleted: Promise<void>;
    readonly appState: AppState;
    readonly authHandlerService: AuthHandlerService;
    readonly tsReactStateHelper: ITsReactStateHelper;
    readonly socketManager: ITechseeSocketManager;
    readonly applicationService: IApplicationService;
    readonly browserUtilsService: IBrowserUtilsExtended;
    readonly localizationService: ILocalizationService;
    readonly redirectionService: IRedirectionService;
    readonly authService: IAuthService;
    readonly dbHistoryService: IHistoryService;
    readonly dbVideoRecordingsService: IVideoRecordingsService;
    readonly historyApiService: IHistoryApiService;
    readonly scanApiService: IScanApiService;
    readonly tsAppUserService: ITsAppUserService;
    readonly eventsLogService: IEventLogsService;
    readonly domUtilsService: IDomUtilsService;
    readonly environmentService: ITsEnvironmentDetect;
    readonly displayTabletAsDesktop: boolean;
    readonly uiFlavorService: IUiFlavorService;
    readonly tsExtraLoggingService: ITsExtraLoggingService;
    readonly authExpiryService: IAuthExpiryService;
    readonly chatApi: IChatApi;
    readonly speedtestService: ISpeedtestService;
    readonly wifiAnalyzerService: IWifiAnalyzerService;
    readonly wifiAnalyzerApiService: IWifiAnalyzerApiService;
    readonly historyConsoleService: IHistoryConsoleService;
    readonly webRtcDetector: IWebRtcDetector;
    readonly mobileClientURL: string;
    readonly accountSocketService: IAccountSocketService;
    readonly jsApiService: IJsApiService;
    readonly timedOperationService: TsTimedOperationService;
    readonly visibilityChangeService: IVisibilityChange;
    readonly interactionSummaryService: IInteractionSummaryService;
    readonly safeApply: any;
    readonly interactionSummaryTranslated: any;
    readonly tsBusyService: ITsBusyService;
    readonly tsResponsiveUtilsService: ITsResponsiveUtilsService;
    readonly eventLogServiceInfra: IEventLogServiceInfra;
    readonly eventLogService: IEventLogService;
    readonly urlConfigService: ITsUrlConfigService;
    readonly translationHelper: ITsTranslationHelper;
    readonly tsInterfaceHelperService: ITsInterfaceHelperService;
    readonly qosService: IQosService;
    readonly agentQosService: IAgentQOSService;
    readonly tsLocaleHelper: ITsLocaleHelperService;
    readonly tsSessionData: ITsSessionData;
    readonly tsCanvasAnnotate: ITsCanvasAnnotateService;
    readonly tsVideoControl: TsVideoControlService;
    readonly accountBrandingService: IAccountBrandingService;
    readonly appEventsHub: AppEventsHub;
    readonly userService: IUserService;
    readonly accountService: IAccountService;
    readonly sessionTagsService: ISessionTagsService;
    readonly shortUrlService: IShortUrlService;
    readonly roomService: IRoomService;
    readonly emailService: IEmailService;
    readonly observationsService: IObservationsService;
    readonly ssoApiService: ISsoApiService;
    readonly resourcesService: IResourcesService;
    readonly s3Service: IS3Service;
    readonly generalApiService: IGeneralApiService;
    readonly smsApiService: ISmsApiService;

    createAgentSettingsController(): AgentSettingsController;
}

declare const API_URL: string;

const apiUrl = getBackendUrl(API_URL, {hostname: window.location.hostname, ENV: {cordova: false}});
const statsServerUrl = getBackendUrl(API_URL, {hostname: window.location.hostname, isStat: true});

export class AppRootStore implements IAppRootStore {
    private _initState: InitializationState = InitializationState.NOT_STARTED;
    private readonly _initRootServicesCompleted: Promise<void>;
    //Global window
    private readonly _window: Window;
    //Angular services
    private _safeApply: any;
    private _tsLocaleHelper?: ITsLocaleHelperService;
    private _dbHistoryService?: IHistoryService;
    private _dbVideoRecordingsService?: IVideoRecordingsService;
    private _tsAppUserService?: ITsAppUserService;
    private _userService?: UserService;
    private _accountService?: IAccountService;
    private _sessionTagsService?: ISessionTagsService;
    private _shortUrlService?: IShortUrlService;
    private _roomService?: IRoomService;
    private _emailService?: IEmailService;
    private _observationsService?: IObservationsService;
    private _resourcesService?: IResourcesService;
    private _s3Service?: IS3Service;
    private _generalApiService?: IGeneralApiService;
    private _initPromiseResolver?: () => void;
    private _appEventsHub?: AppEventsHub;

    private _authApiService?: IAuthApiService;
    private _authService?: IAuthService;
    private _eventsLogService?: IEventLogsService;
    private _applicationService?: IApplicationService;
    private _browserUtilsService?: IBrowserUtilsExtended;
    private _environmentService?: ITsEnvironmentDetect;
    private _localizationService?: ILocalizationService;
    private _redirectionService?: IRedirectionService;
    private _domUtilsService?: IDomUtilsService;
    private _uiFlavorService?: IUiFlavorService;
    private _tsExtraLoggingService?: ITsExtraLoggingService;
    private _authExpiryService?: IAuthExpiryService;
    private _chatApi?: IChatApi;
    private _speedtestService?: ISpeedtestService;
    private _wifiAnalyzerService?: IWifiAnalyzerService;
    private _webRtcDetector?: IWebRtcDetector;
    private _socketManager?: ITechseeSocketManager;
    private _accountSocketService?: IAccountSocketService;
    private _jsApiService?: IJsApiService;
    private _socketUrl: string;
    private _mobileClientURL: string;
    private _timedOperation?: TsTimedOperationService;
    private _visibilityChangeService?: IVisibilityChange;
    private _appState?: AppState;
    private _interactionSummaryService?: IInteractionSummaryService;
    private _tsBusyService?: ITsBusyService;
    private _tsResponsiveUtilsService?: ITsResponsiveUtilsService;
    private _eventLogServiceInfra?: IEventLogServiceInfra;
    private _eventLogService?: IEventLogService;
    private _urlConfigService?: ITsUrlConfigService;
    private _customStringApiService?: ICustomStringService;
    private _translationHelper?: ITsTranslationHelper;
    private _tsInterfaceHelperService?: ITsInterfaceHelperService;
    private _qosService?: IQosService;
    private _wifiAnalyzerApiService?: IWifiAnalyzerApiService;
    private _historyConsoleService?: IHistoryConsoleService;
    private _agentQosService?: IAgentQOSService;
    private _tsSessionData?: ITsSessionData;
    private _tsCanvasAnnotate?: ITsCanvasAnnotateService;
    private _tsVideoControl?: TsVideoControlService;
    private _accountBrandingService?: IAccountBrandingService;
    private _historyApiService?: IHistoryApiService;
    private _ssoApiService?: ISsoApiService;
    private _scanApiService?: IScanApiService;
    private _smsApiService?: ISmsApiService;
    private _authHandlerService?: AuthHandlerService;
    private _tsReactStateHelper?: ITsReactStateHelper;

    constructor(bootStrapParams: IAppBootstrapParams) {
        this._window = bootStrapParams.globals.window;
        this._socketUrl = bootStrapParams.config.socketUrl;
        this._mobileClientURL = bootStrapParams.config.mobileClientURL;
        this._environmentService = bootStrapParams.dependencies?.environmentDetect;
        this._initRootServicesCompleted = new Promise((resolve) => (this._initPromiseResolver = resolve));
    }

    setSafeApply(safeApply: any) {
        this._safeApply = safeApply;
    }

    async initRootServices(settings: AppStoreSettings): Promise<void> {
        if (this._initState !== InitializationState.NOT_STARTED) {
            return;
        }

        // Initialize core services first
        this._eventLogServiceInfra = new EventLogServiceInfra(statsServerUrl);
        this._appEventsHub = new AppEventsHub();

        this._webRtcDetector = new DetectWebRtcService(DETECT_RTC_ADVISED_TIMEOUT);
        this._browserUtilsService = new BrowserUtilsExtended(new BrowserUtilsService(this._window));
        this._tsLocaleHelper = new TsLocaleHelperService(this._browserUtilsService);
        this._chatApi = new ChatApi(this._tsLocaleHelper);

        this._initState = InitializationState.INITIALIZING;
        this._appState = this.initAppState();

        // _events Log Service
        this._eventsLogService = new EventLogsService(this._eventLogServiceInfra, EVENT_SOURCES.dashboard);
        // _event Log Service
        this._eventLogService = new EventLogService(this._eventLogServiceInfra);
        this._authApiService = new AuthApiService(apiUrl);
        this._authService = new AuthService(this._browserUtilsService, this._authApiService);

        const authToken = () => this._authService?.getAuthToken() || '';

        // Initialize dbUserService early since it's a dependency for other services
        this._qosService = new QosService(statsServerUrl);
        this._userService = new UserService(apiUrl, authToken);
        this._accountService = new AccountService(apiUrl, authToken);
        this._sessionTagsService = new SessionTagsService(apiUrl, authToken);
        this._shortUrlService = new ShortUrlService(apiUrl, authToken);
        this._roomService = new RoomService(apiUrl, authToken, this._eventLogServiceInfra);
        this._emailService = new EmailService(apiUrl, authToken);
        this._observationsService = new ObservationsService(apiUrl, authToken);
        this._wifiAnalyzerApiService = new WifiAnalyzerApiService(apiUrl, authToken);
        this._historyConsoleService = new HistoryConsoleService(apiUrl, authToken);
        this._resourcesService = new ResourcesServices(apiUrl, authToken);
        this._s3Service = new S3Service(apiUrl, authToken);
        this._generalApiService = new GeneralApiService(apiUrl, authToken);
        this._historyApiService = new HistoryApiService(apiUrl, authToken);
        this._ssoApiService = new SsoApiService(apiUrl, authToken);
        this._scanApiService = new ScanApiService(apiUrl, authToken);
        this._smsApiService = new SmsApiService(authToken, apiUrl);

        this._authService.on('newToken', () => {
            const newToken = this._authService!.getAuthToken() || '';
            this._userService = new UserService(apiUrl, newToken);
        });
        this._agentQosService = new AgentQOSService();
        this._tsSessionData = new TsSessionData();

        this._uiFlavorService = new UiFlavorService(
            this._browserUtilsService,
            this._accountService as IAccountService,
            this._environmentService!
        );

        this._urlConfigService = new TsUrlConfigService(this._browserUtilsService, settings.env);

        this.urlConfigService.init();

        this._customStringApiService = new CustomStringService(apiUrl);
        this._localizationService = new LocalizationService(
            this._customStringApiService,
            this._browserUtilsService,
            (key, language) => this._eventLogService?.translationsFallbackToEnglish({translations_key: key, language})
        );
        this._accountBrandingService = new AccountBrandingService(apiUrl);
        await this._localizationService.init();

        this._redirectionService = new RedirectionService(
            this._window,
            this._generalApiService!,
            this._browserUtilsService,
            this._eventsLogService,
            this._roomService as IRoomService
        );

        this._dbHistoryService = new TsAppHistoryService(apiUrl, authToken);
        this._dbVideoRecordingsService = new TsAppVideoRecordingsService(apiUrl, authToken);
        this._tsAppUserService = new TsAppUserService(apiUrl, this._authService);

        this._speedtestService = new SpeedtestService();

        this._timedOperation = new TsTimedOperationService();
        this._visibilityChangeService = new TsVisibilityChange(this._window);
        this._translationHelper = new TsTranslationHelper(this._customStringApiService);
        this._tsVideoControl = new TsVideoControlService(this._eventLogService);

        const defaultSocketConfig = TechseeSocketManager.getDefaultConfig(this._socketUrl);

        defaultSocketConfig.browserUtilsService = this._browserUtilsService;

        const socketConfig = this._browserUtilsService.getFromSessionStorage('socketConfig');

        if (socketConfig) {
            defaultSocketConfig.socketPath = socketConfig.path;
            defaultSocketConfig.sessionChannel.namespace = socketConfig.namespace;
        }

        TechseeSocketManager.setConfig(defaultSocketConfig);
        this._socketManager = TechseeSocketManager.instance();

        this._chatApi.setBrowserInfo({userAgent: this._window.navigator.userAgent});
        this._chatApi.setTechseeSocketManager(this._socketManager);
        this._chatApi.setSafeApply(this._safeApply);
        this._chatApi.init();
        this._chatApi.on('imageLoaded', (meta: object) => {
            this._eventsLogService?.info({
                roomId: this._chatApi?.roomId,
                userId: this._chatApi?.userId,
                logType: LOG_EVENTS.agentLoadedCustomerImage,
                meta
            });
        });

        this._chatApi.on('imageSendingFailed', (meta: object) => {
            this._eventsLogService?.error({
                roomId: this._chatApi?.roomId,
                userId: this._chatApi?.userId,
                logType: LOG_EVENTS.imageSendingFailed,
                meta
            });
        });

        this._accountSocketService = new AccountSocketService(
            this._socketManager.accountSocket,
            this._browserUtilsService,
            this._appState,
            this._userService,
            this._eventsLogService
        );

        this._applicationService = new ApplicationService(
            this._appState,
            this._authService,
            this._accountSocketService.joinAccountChannel.bind(this._accountSocketService),
            this._accountSocketService.disconnectAccountChannel.bind(this._accountSocketService)
        );

        this._authExpiryService = new AuthExpiryService(
            this._localizationService?.translate,
            this._applicationService,
            this._redirectionService,
            this._browserUtilsService,
            createFieldModel,
            this._eventsLogService,
            this._userService
        );

        this._jsApiService = new JsApiService(
            this._authExpiryService,
            this._eventsLogService,
            this._browserUtilsService
        );

        this._wifiAnalyzerService = new WifiAnalyzerService(
            this._scanApiService!,
            this._roomService,
            this._eventsLogService,
            this._wifiAnalyzerApiService
        );
        this._tsBusyService = new TsBusyService();
        this._tsResponsiveUtilsService = new TsResponsiveUtilsService(this._window);

        this._tsInterfaceHelperService = new TsInterfaceHelperService(
            this._browserUtilsService,
            this._tsResponsiveUtilsService,
            this._environmentService!
        );

        this._tsExtraLoggingService = new TsExtraLoggingService();
        this._tsCanvasAnnotate = new TsCanvasAnnotateService();

        // Initialize auth handler service with specific services
        this._authHandlerService = new AuthHandlerService(
            this._authService,
            this._redirectionService,
            this._userService,
            this._ssoApiService,
            this._browserUtilsService,
            this._uiFlavorService
        );

        // Initialize React state helper (unique name - can be renamed to tsStateHelper later when Angular is removed)
        this._tsReactStateHelper = new TsReactStateHelper();

        this._initState = InitializationState.COMPLETED;

        this._initPromiseResolver!();

        return this.initRootServicesCompleted;
    }

    get appState(): AppState {
        if (!this._appState) {
            throw new Error('AppState not initialized');
        }

        return this._appState;
    }

    get applicationService(): IApplicationService {
        if (!this._applicationService) {
            throw new Error('ApplicationService not initialized');
        }

        return this._applicationService;
    }

    get chatApi(): IChatApi {
        if (!this._chatApi) {
            throw new Error('ChatApi not initialized');
        }
        return this._chatApi;
    }

    get qosService(): IQosService {
        if (!this._qosService) {
            throw new Error('qosService not initialized');
        }

        return this._qosService;
    }

    get tsCanvasAnnotate(): ITsCanvasAnnotateService {
        if (!this._tsCanvasAnnotate) {
            throw new Error('tsCanvasAnnotate not initialized');
        }

        return this._tsCanvasAnnotate;
    }

    get tsSessionData(): ITsSessionData {
        if (!this._tsSessionData) {
            throw new Error('tsSessionData not initialized');
        }

        return this._tsSessionData;
    }

    get tsLocaleHelper(): ITsLocaleHelperService {
        if (!this._tsLocaleHelper) {
            throw new Error('tsLocaleHelper not initialized');
        }

        return this._tsLocaleHelper;
    }

    get agentQosService(): IAgentQOSService {
        if (!this._agentQosService) {
            throw new Error('qosService not initialized');
        }

        return this._agentQosService;
    }

    get localizationService(): ILocalizationService {
        if (!this._localizationService) {
            throw new Error('localizationService not initialized');
        }

        return this._localizationService;
    }

    get browserUtilsService(): IBrowserUtilsExtended {
        if (!this._browserUtilsService) {
            throw new Error('browserUtilsService not initialized');
        }

        return this._browserUtilsService;
    }

    get environmentService(): ITsEnvironmentDetect {
        if (!this._environmentService) {
            throw new Error('tsEnvironmentDetect service not initialized');
        }

        return this._environmentService;
    }

    get displayTabletAsDesktop() {
        return this._browserUtilsService && this._browserUtilsService.getFromLocalStorage('displayTabletAsDesktop');
    }

    get domUtilsService(): IDomUtilsService {
        if (!this._domUtilsService) {
            this._domUtilsService = new DomUtilsService();
        }

        return this._domUtilsService;
    }

    get interactionSummaryService(): IInteractionSummaryService {
        if (!this._interactionSummaryService) {
            const baseRoot = this.urlConfigService.get('API_URL');
            const token = this._authService?.getAuthToken() || '';

            this._interactionSummaryService = new InteractionSummaryService(baseRoot, token);
        }

        return this._interactionSummaryService;
    }

    get redirectionService(): IRedirectionService {
        if (!this._redirectionService) {
            throw new Error('redirectionService not initialized');
        }

        return this._redirectionService;
    }

    get eventsLogService() {
        if (!this._eventsLogService) {
            throw new Error('eventsLogService not initialized');
        }

        return this._eventsLogService;
    }

    get dbHistoryService(): IHistoryService {
        if (!this._dbHistoryService) {
            throw new Error('_dbHistoryService not initialized');
        }

        return this._dbHistoryService;
    }

    get dbVideoRecordingsService(): any {
        if (!this._dbVideoRecordingsService) {
            throw new Error('_dbVideoRecordingsService not initialized');
        }

        return this._dbVideoRecordingsService;
    }

    get tsAppUserService(): ITsAppUserService {
        if (!this._tsAppUserService) {
            throw new Error('_tsAppUserService not initialized');
        }

        return this._tsAppUserService;
    }

    get authService(): IAuthService {
        if (!this._authService) {
            throw new Error('authService not initialized');
        }

        return this._authService;
    }

    get uiFlavorService(): IUiFlavorService {
        if (!this._uiFlavorService) {
            throw new Error('uiFlavorService not initialized');
        }

        return this._uiFlavorService;
    }

    get authExpiryService(): IAuthExpiryService {
        if (!this._authExpiryService) {
            throw new Error('authExpiryService not initialized');
        }

        return this._authExpiryService;
    }

    get speedtestService(): ISpeedtestService {
        if (!this._speedtestService) {
            throw new Error('speedtestService not initialized');
        }

        return this._speedtestService;
    }

    get wifiAnalyzerService(): IWifiAnalyzerService {
        if (!this._wifiAnalyzerService) {
            throw new Error('wifiAnalyzerService not initialized');
        }

        return this._wifiAnalyzerService;
    }

    get wifiAnalyzerApiService(): IWifiAnalyzerApiService {
        if (!this._wifiAnalyzerApiService) {
            throw new Error('wifiAnalyzerApiService not initialized');
        }

        return this._wifiAnalyzerApiService;
    }

    get historyConsoleService(): IHistoryConsoleService {
        if (!this._historyConsoleService) {
            throw new Error('historyConsoleService not initialized');
        }

        return this._historyConsoleService;
    }

    get webRtcDetector(): IWebRtcDetector {
        if (!this._webRtcDetector) {
            throw new Error('webRtcDetector not initialized');
        }

        return this._webRtcDetector;
    }

    get globals() {
        return {
            window: this._window
        };
    }

    get socketManager(): ITechseeSocketManager {
        if (!this._socketManager) {
            throw new Error('socketManager not initialized');
        }

        return this._socketManager;
    }

    get accountSocketService(): IAccountSocketService {
        if (!this._accountSocketService) {
            throw new Error('accountSocketService not initialized');
        }

        return this._accountSocketService;
    }

    get timedOperationService(): TsTimedOperationService {
        if (!this._timedOperation) {
            throw new Error('timedOperationService not initialized');
        }

        return this._timedOperation;
    }

    get visibilityChangeService(): IVisibilityChange {
        if (!this._visibilityChangeService) {
            throw new Error('visibilityChangeService not initialized');
        }

        return this._visibilityChangeService;
    }

    get jsApiService(): IJsApiService {
        if (!this._jsApiService) {
            throw new Error('jsApiService not initialized');
        }

        return this._jsApiService;
    }

    get mobileClientURL(): string {
        return this._mobileClientURL;
    }

    get safeApply() {
        return this._safeApply;
    }

    get interactionSummaryTranslated() {
        return {
            collapseAll: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.COLLAPSE_ALL'),
            expandAll: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.EXPAND_ALL'),
            noData: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.NO_DATA'),
            unknownError: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.UNKNOWN_ERROR'),
            refresh: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.REFRESH'),
            textAnalysis: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.TEXT.ANALYSIS'),
            [ScreenType.selectFlow]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.SELECT_FLOW'
            ),
            [ScreenType.capture]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.CAPTURE'
            ),
            [ScreenType.addImage]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.ADD_IMAGE'
            ),
            [ScreenType.addFile]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.ADD_FILE'
            ),
            ADD_FILE_VIDEO: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.ADD_FILE_VIDEO'
            ),
            [ScreenType.recordAudio]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.RECORD_AUDIO'
            ),
            [ScreenType.textInput]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.TEXT_INPUT'
            ),
            [ScreenType.externalAPICall]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.API_CALL'
            ),
            [ScreenType.scan]: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.SCAN'),
            [ScreenType.textProcessor]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.TEXT_PROCESSOR'
            ),
            [ScreenType.imageAnalysis]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.IMAGE_ANALYSIS'
            ),
            [ScreenType.keyValue]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.KEY_VALUE'
            ),
            [ScreenType.form]: this._localizationService?.translate('REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.FORM'),
            [ScreenType.speedTest]: this._localizationService?.translate(
                'REACT.INTERACTION.SUMMARY.DISPLAY.BLOCK.SPEEDTEST'
            )
        };
    }

    getDateFormattedByAcc(date: string, format: string) {
        const dateFormat = format.split(' ')[0].toUpperCase();
        const timeFormat = format.split(' ')[1];
        const newFormat = dateFormat + (timeFormat ? ' ' + timeFormat : '');

        return tsDate(new Date(date)).utc().format(newFormat);
    }

    getDateFormattedByAccLocal(date: string, format: string) {
        const dateFormat = format.split(' ')[0].toUpperCase();
        const timeFormat = format.split(' ')[1];
        const newFormat = dateFormat + (timeFormat ? ' ' + timeFormat : '');

        return tsDate(new Date(date)).format(newFormat);
    }

    @action
    private initAppState(): AppState {
        if (!this._appState) {
            this._appState = new AppState();
            this._appState.dashboardLanguage = 'en_US';
        }

        return this._appState;
    }

    get tsBusyService(): ITsBusyService {
        if (!this._tsBusyService) {
            throw new Error('tsBusyService not initialized');
        }

        return this._tsBusyService;
    }

    get tsResponsiveUtilsService(): ITsResponsiveUtilsService {
        if (!this._tsResponsiveUtilsService) {
            throw new Error('tsResponsiveUtilsService not initialized');
        }

        return this._tsResponsiveUtilsService;
    }

    get eventLogServiceInfra(): IEventLogServiceInfra {
        if (!this._eventLogServiceInfra) {
            throw new Error('eventLogServiceInfra not initialized');
        }

        return this._eventLogServiceInfra;
    }

    get eventLogService(): IEventLogService {
        if (!this._eventLogService) {
            throw new Error('eventLogService not initialized');
        }

        return this._eventLogService;
    }

    get urlConfigService(): ITsUrlConfigService {
        if (!this._urlConfigService) {
            throw new Error('urlConfigService not initialized');
        }

        return this._urlConfigService;
    }

    get translationHelper(): ITsTranslationHelper {
        if (!this._translationHelper) {
            throw new Error('translationHelper not initialized');
        }

        return this._translationHelper;
    }

    get tsInterfaceHelperService(): ITsInterfaceHelperService {
        if (!this._tsInterfaceHelperService) {
            throw new Error('Interface helper service not initialized');
        }
        return this._tsInterfaceHelperService;
    }

    get initRootServicesCompleted(): Promise<void> {
        return this._initRootServicesCompleted;
    }

    get tsExtraLoggingService(): ITsExtraLoggingService {
        if (!this._tsExtraLoggingService) {
            throw new Error('tsExtraLoggingService not initialized');
        }
        return this._tsExtraLoggingService;
    }

    get tsVideoControl(): TsVideoControlService {
        if (!this._tsVideoControl) {
            throw new Error('tsVideoControl not initialized');
        }
        return this._tsVideoControl;
    }

    get accountBrandingService(): IAccountBrandingService {
        if (!this._accountBrandingService) {
            throw new Error('accountBrandingService not initialized');
        }
        return this._accountBrandingService;
    }

    get appEventsHub(): AppEventsHub {
        if (!this._appEventsHub) {
            throw new Error('appEventsHub not initialized');
        }
        return this._appEventsHub;
    }

    get userService(): IUserService {
        if (!this._userService) {
            throw new Error('userService not initialized');
        }
        return this._userService;
    }

    get accountService(): IAccountService {
        if (!this._accountService) {
            throw new Error('accountService not initialized');
        }
        return this._accountService;
    }

    get sessionTagsService(): ISessionTagsService {
        if (!this._sessionTagsService) {
            throw new Error('sessionTagsService not initialized');
        }
        return this._sessionTagsService;
    }

    get shortUrlService(): IShortUrlService {
        if (!this._shortUrlService) {
            throw new Error('shortUrlService not initialized');
        }
        return this._shortUrlService;
    }

    get roomService(): IRoomService {
        if (!this._roomService) {
            throw new Error('roomService not initialized');
        }
        return this._roomService;
    }

    get emailService(): IEmailService {
        if (!this._emailService) {
            throw new Error('emailService not initialized');
        }
        return this._emailService;
    }

    get observationsService(): IObservationsService {
        if (!this._observationsService) {
            throw new Error('observationsService not initialized');
        }
        return this._observationsService;
    }

    get historyApiService(): IHistoryApiService {
        if (!this._historyApiService) {
            throw new Error('historyApiService not initialized');
        }
        return this._historyApiService;
    }

    get ssoApiService(): ISsoApiService {
        if (!this._ssoApiService) {
            throw new Error('ssoApiService not initialized');
        }
        return this._ssoApiService;
    }

    get scanApiService(): IScanApiService {
        if (!this._scanApiService) {
            throw new Error('scanApiService not initialized');
        }
        return this._scanApiService;
    }

    get resourcesService(): IResourcesService {
        if (!this._resourcesService) {
            throw new Error('resourcesService not initialized');
        }
        return this._resourcesService;
    }

    get s3Service(): IS3Service {
        if (!this._s3Service) {
            throw new Error('s3Service not initialized');
        }
        return this._s3Service;
    }

    get generalApiService(): IGeneralApiService {
        if (!this._generalApiService) {
            throw new Error('generalApiService not initialized');
        }
        return this._generalApiService;
    }

    get smsApiService(): ISmsApiService {
        if (!this._smsApiService) {
            throw new Error('smsApiService not initialized');
        }
        return this._smsApiService;
    }

    get authHandlerService(): AuthHandlerService {
        if (!this._authHandlerService) {
            throw new Error('authHandlerService not initialized');
        }
        return this._authHandlerService;
    }

    get tsReactStateHelper(): ITsReactStateHelper {
        if (!this._tsReactStateHelper) {
            throw new Error('tsReactStateHelper not initialized');
        }
        return this._tsReactStateHelper;
    }

    createAgentSettingsController(): AgentSettingsController {
        return new AgentSettingsController();
    }
}
